import protobuf from 'protobufjs';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { ECexName } from 'web3';

import { CexAdapter, SubscribeParams, UnsubscribeHandler } from '../../model/types';

const WS_URL = 'wss://wbs-api.mexc.com/ws'; // Protobuf WebSocket for trades
const PING_INTERVAL_MS = 60_000;

type MexcSubscriptionResponse = {
  code?: number;
  msg?: string;
  id?: number;
};

// Load protobuf schema
let messageProto: protobuf.Type | undefined;
const isTestEnv = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

if (!isTestEnv) {
  fetch('/mexc-protos/protos.proto')
    .then(async res => {
      const protoText = await res.text();
      const root = protobuf.parse(protoText).root;
      messageProto = root.lookupType('PushDataV3ApiWrapper');
      console.log('[MexcAdapter] ✅ Protobuf schema loaded');
    })
    .catch(error => {
      console.error('[MexcAdapter] ❌ Failed to load protobuf schema:', error);
    });
} else {
  console.warn('[MexcAdapter] Skipping protobuf schema load in test environment');
}

export const createMexcAdapter = (): CexAdapter => ({
  exchange: ECexName.mexc,

  subscribe({ instrument, onTick }: SubscribeParams): UnsubscribeHandler {
    const symbol = `${instrument.base}${instrument.quote}`.toUpperCase();
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let tickCount = 0;

    // Use protobuf trade stream (100ms aggregated deals)
    const channel = `spot@public.aggre.deals.v3.api.pb@100ms@${symbol}`;

    console.log(`[MexcAdapter] 🔔 Subscribing to ${channel}`);

    const ws = new ReconnectingWebSocket(WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    ws.addEventListener('open', () => {
      console.log(`[MexcAdapter] ✅ WebSocket connected`);

      ws.send(
        JSON.stringify({
          method: 'SUBSCRIPTION',
          params: [channel],
        }),
      );

      console.log(`[MexcAdapter] ✅ Subscription sent for ${channel}`);

      // Setup ping interval
      pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ method: 'PING' }));
        }
      }, PING_INTERVAL_MS);
    });

    const handleMessage = (event: MessageEvent) => {
      // Check if it's a JSON response (subscription confirmation or error)
      if (typeof event.data === 'string') {
        try {
          const response: MexcSubscriptionResponse = JSON.parse(event.data);

          // Check for subscription errors
          if ('code' in response && 'msg' in response) {
            if (response.code !== 0 || response.msg?.includes('Not Subscribed')) {
              console.error(`[MexcAdapter] ❌ Subscription error:`, response);
              return;
            }
            console.log(`[MexcAdapter] ✅ Subscription confirmed:`, response);
          }
        } catch (error) {
          console.error('[MexcAdapter] Failed to parse JSON response', error);
        }
        return;
      }

      // Handle protobuf binary data
      if (!messageProto) {
        console.warn('[MexcAdapter] ⚠️ Protobuf schema not loaded yet');
        return;
      }

      if (event.data instanceof Blob) {
        event.data
          .arrayBuffer()
          .then(arrayBuffer => {
            const buffer = new Uint8Array(arrayBuffer);

            const messBuff = messageProto!.decode(buffer);
            const message: any = messageProto!.toObject(messBuff, {
              longs: String,
              enums: String,
              bytes: String,
            });

            if (!message || message.channel !== channel) return;

            // Extract trade data from protobuf message
            const tradeData = message.publicAggreDeals?.dealsList?.[0];
            if (!tradeData) return;

            const price = Number(tradeData.price);
            const time = Number(tradeData.time);
            const quantity = Number(tradeData.quantity);

            if (!Number.isFinite(price) || !Number.isFinite(time)) return;

            tickCount++;
            if (tickCount <= 5 || tickCount % 10 === 0) {
              console.log(
                `[MexcAdapter] 📊 Tick #${tickCount} for ${channel}: price=${price}, time=${new Date(
                  time,
                ).toISOString()}, quantity=${quantity}`,
              );
            }

            onTick({
              time,
              price,
              volume: quantity || 0,
            });
          })
          .catch(error => {
            console.error('[MexcAdapter] Failed to parse protobuf message', error);
          });
      }
    };

    const handleError = (error: any) => {
      console.error('[MexcAdapter] ❌ WebSocket error', error);
    };

    const handleClose = (event: any) => {
      console.log(`[MexcAdapter] 🔌 WebSocket closed: code=${event.code}, reason=${event.reason}`);

      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
    };

    ws.addEventListener('message', handleMessage);
    ws.addEventListener('error', handleError);
    ws.addEventListener('close', handleClose);

    return () => {
      console.log(`[MexcAdapter] 🛑 Unsubscribing from ${channel}`);

      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }

      ws.close();
    };
  },
});
